import { Box, ClickAwayListener, Popover, Typography } from "@material-ui/core";
import { NavigateNext } from "@material-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { firstLetterUppercase } from "../../utils/first-letter-uppercase";
import ListFilter from "./ListFilter";
import { ListFilterState, TableFilterAction } from "./models";
import useStyles from "./styles";

interface INestedListFilterProps {
	filter: ListFilterState;
	parentCode?: string;
	usePopover?: boolean;
	dispatcher: (action: TableFilterAction) => void;
}

const NestedListFilter: React.FC<INestedListFilterProps> = ({ filter, parentCode, usePopover, dispatcher }) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef(null);
	const nestedListRef = useRef(null);
	const { nestedDialogFilter, popover, popoverContent } = useStyles();
	const { t } = useTranslation();

	const handlePopoverOpen = () => {
		setIsOpen(true);
	};

 const handlePopoverClose = () => {
		setIsOpen(false);
	};

	useEffect(() => {
		if (!filter.dontHideOnMouseLeave) {
			return;
		}

		let previousX = 0;
		let previousY = 0;

		const getDistance = (x1: number, y1: number, x2: number, y2: number) => {
			return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
		};

		const handleGlobalMove = (e: MouseEvent) => {
			if (!nestedListRef.current) {
				return;
			}

			const box = (nestedListRef.current as HTMLDivElement).getBoundingClientRect();
			const { clientX: x, clientY: y } = e;

			const inside = x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;

			const prevDistance = getDistance(box.x + box.width / 2, box.y, previousX, previousY);
			const currDistance = getDistance(box.x + box.width / 2, box.y, x, y);
			previousX = x;
			previousY = y;
			const threshold = 3;
			if (!inside && currDistance > prevDistance + threshold) {
				setIsOpen(false);
			}
		};

		if (!isOpen) {
			return;
		}

		window.addEventListener("mousemove", handleGlobalMove);
		return () => window.removeEventListener("mousemove", handleGlobalMove);
	}, [nestedListRef.current, isOpen, filter.dontHideOnMouseLeave]);

	return usePopover ? (
		<ClickAwayListener onClickAway={handlePopoverClose}>
			<Box>
				<Typography
					ref={ref}
					aria-haspopup="true"
					onMouseEnter={handlePopoverOpen}
					onMouseLeave={handlePopoverClose}
					color="primary"
				>
					{filter.label && t(filter.label)} <NavigateNext fontSize="small" style={{ verticalAlign: "middle" }} />
				</Typography>
				<Popover

					open={isOpen}
					anchorEl={ref.current}
					anchorOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "left",
					}}
					className={popover}
					classes={{ paper: popoverContent }}
					onClose={handlePopoverClose}
					PaperProps={{
						onMouseEnter: handlePopoverOpen,
						onMouseLeave: filter.dontHideOnMouseLeave ? undefined : handlePopoverClose,
					}}
					disableRestoreFocus
				>
					<div ref={nestedListRef}>
						<ListFilter filter={filter} usePopover dispatcher={dispatcher} nested />
					</div>
				</Popover>
			</Box>
		</ClickAwayListener>
	) : (
		<Box className={nestedDialogFilter}>
			{filter.label && <Typography color="textSecondary">{firstLetterUppercase(t(filter.label))}</Typography>}
			<ListFilter filter={filter} parentCode={parentCode} usePopover={false} dispatcher={dispatcher} nested />
		</Box>
	);
};

export default NestedListFilter;
